import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ListShowMoreIcon } from '../../../Svgs'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../store'
import { SceneManager } from '../../../../visualization/sceneManager'
import { createSelector } from '@reduxjs/toolkit'
import { BuildingViewMode } from '../../../../models/Construction'
import {
  computeBoundingBox,
  isStoreyMesh,
  isWallMesh,
} from '../../../../visualization/utils'
import { updateBuildingElements } from '../../../../utils/ConstructionUtil'
import { updateBuildingInfo } from '../../../../services/buildingService'
import { updateStatusMessage } from '../../../../actions/construction'

const convertDataSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.convertData,
)

const storeyHeightSelector = createSelector(
  [
    (state: RootState) => state.construction.selectedBuildingMode,
    (state: RootState) => state.construction.selectedStoreyId,
  ],
  (buildingMode, selectedStoreyId) => {
    if (buildingMode !== BuildingViewMode.SingleStorey || !selectedStoreyId) {
      return 0
    }

    const scene = SceneManager.getInstance().getScene()
    const storeyMesh = scene?.getMeshById(selectedStoreyId)

    if (!storeyMesh || !isStoreyMesh(storeyMesh)) {
      return 0
    }

    const wallMeshes = storeyMesh.getChildMeshes().filter((m) => isWallMesh(m))
    const wallsBoundingBox = computeBoundingBox(wallMeshes)

    return wallsBoundingBox.max.y - wallsBoundingBox.min.y
  },
)

const FloorHeight = () => {
  const dispatch = useDispatch<any>()
  const selectedStoreyHeight = useSelector(storeyHeightSelector)
  const convertData = useSelector(convertDataSelector)
  const selectedStoreyId = useSelector(
    (state: RootState) => state.construction.selectedStoreyId,
  )
  const [isOpen, setIsOpen] = useState(true)
  const [floorHeight, setFloorHeight] = useState<string>(
    selectedStoreyHeight.toString(),
  )
  const [debouncedFloorHeight, setDebouncedFloorHeight] = useState(floorHeight)

  useEffect(() => {
    const height = selectedStoreyHeight.toString()
    setFloorHeight(height)
    setDebouncedFloorHeight(height)
  }, [selectedStoreyId])

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (Number(floorHeight) === Number(debouncedFloorHeight)) {
        return
      }

      if (isNaN(Number(floorHeight))) {
        return
      }

      dispatch(updateStatusMessage('Updating floor height...'))
      setDebouncedFloorHeight(floorHeight)

      const selectedStoreyIndex = convertData.building.storeys.findIndex(
        (storey) => storey.id === selectedStoreyId,
      )
      if (selectedStoreyIndex !== -1) {
        const newData: {
          elementId: string
          propertyName: string
          propertyValue: string
        }[] = []
        const selectedWallsIds = convertData.building.storeys[
          selectedStoreyIndex
        ].walls.map((wall) => wall.id)
        selectedWallsIds.forEach((wallId) =>
          newData.push({
            elementId: wallId,
            propertyName: 'height',
            propertyValue: floorHeight,
          }),
        )

        const heightDiff =
          parseFloat(floorHeight) - parseFloat(debouncedFloorHeight)
        const higherStoreys = convertData.building.storeys.filter(
          (storey, index) =>
            storey.id !== selectedStoreyId && index > selectedStoreyIndex,
        )
        higherStoreys.forEach((storey) => {
          storey.walls.forEach((wall) =>
            newData.push({
              elementId: wall.id,
              propertyName: 'bottom_elevation',
              propertyValue: (wall.bottom_elevation + heightDiff).toString(),
            }),
          )
          storey.rooms.forEach((room) =>
            newData.push({
              elementId: room.floor.id,
              propertyName: 'elevation',
              propertyValue: (room.floor.elevation + heightDiff).toString(),
            }),
          )
        })

        const updatedBuilding = updateBuildingElements(
          structuredClone(convertData.building),
          newData,
        )
        updatedBuilding.storeys[selectedStoreyIndex].height =
          parseFloat(floorHeight)

        await dispatch(
          updateBuildingInfo({
            fileKey: convertData.json_s3_key,
            building_json: { ...convertData, building: updatedBuilding },
            elementId: null,
          }),
        ).unwrap()
      }
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [floorHeight])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '4px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          color='#04002E'
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Selected Floor Settings
        </Typography>

        <IconButton
          onClick={() => setIsOpen((prev) => !prev)}
          sx={{
            transition: 'transform 0.3s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(180deg)',
          }}
        >
          <ListShowMoreIcon
            sx={{
              fontSize: '14px',
              color: '#797979',
            }}
          />
        </IconButton>
      </Box>

      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <Stack
          direction='column'
          sx={{
            padding: '8px 12px',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '50%',
                fontSize: '13px',
                color: '#737373',
                lineHeight: 'normal',
                textAlign: 'start',
                textTransform: 'capitalize',
              }}
            >
              Height
            </Typography>

            <TextField
              margin='none'
              size='small'
              type='number'
              fullWidth
              value={floorHeight}
              onChange={(e) => setFloorHeight(e.target.value)}
              error={floorHeight === '' || isNaN(Number(floorHeight))}
              helperText={
                floorHeight === '' || isNaN(Number(floorHeight))
                  ? 'Please enter a valid number'
                  : ''
              }
              sx={{
                background: '#F5F5F5',
                borderRadius: '4px',
                '& .MuiInputBase-input': {
                  fontSize: '13px',
                  color: '#737373',
                  lineHeight: 'normal',
                  p: '6px 8px',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
              }}
            ></TextField>
          </Box>
        </Stack>

        <Divider
          flexItem
          orientation='horizontal'
          sx={{
            backgroundColor: '#E4E4E4',
          }}
        />
      </Collapse>
    </Box>
  )
}

export default FloorHeight
